import {
  ROLE_DEFAULT_CAPABILITIES,
  type AuthUser,
  type Capability,
} from '@admitly/shared';

/**
 * What the signed-in user may DO — the browser's half of the capability model
 * (Brief 8b). Every sidebar link and route guard that used to ask "what role are
 * you?" asks one of these predicates instead.
 *
 * The bug this exists for: a master admin flipped every switch ON for consultant
 * Akanksha. The server honoured them from the first request (requireCapability
 * reads overrides fresh from the DB); Brief 8a put her effective set on the user
 * object the client receives; but her sidebar didn't move, because every gate
 * read `user.role`. A capability she holds and cannot reach is not a capability.
 *
 * THE SERVER IS STILL THE ONLY GATE. `requireCapability` (apps/api
 * middleware/authz.ts) decides every request on its own. Nothing here grants
 * access — these predicates only decide what is worth SHOWING. A user who edits
 * this bundle in their browser gains links, not permission.
 *
 * Pure functions over the user object, no store import, so route guards, the
 * Sidebar and the tests all read one source of truth.
 */

/**
 * The user's effective capabilities, as a Set.
 *
 * TWO POPULATIONS, ONE TYPE (see AuthUser.capabilities): the DB-derived payload
 * from login / refresh / GET /auth/me ALWAYS carries the field, while a
 * token-derived AuthUser never does. So `capabilities: undefined` means "not
 * told", NOT "holds nothing" — falling back to the role preset is what keeps
 * those two populations agreeing, because for an un-overridden user the preset
 * IS their effective set (pinned server-side: auth-capabilities.test.ts (a)).
 *
 * Deny-on-unknown would be the wrong instinct here: this is a display rule, not
 * a gate, and denying would blank the sidebar of every user the field is simply
 * absent for.
 *
 * Null user FIRST, before indexing the preset map — a signed-out visitor reaches
 * the route guards with `user === null`, and indexing `[undefined]` throws a
 * TypeError instead of redirecting them to /login.
 */
export function myCapabilities(user: AuthUser | null | undefined): ReadonlySet<Capability> {
  if (!user) return new Set<Capability>();
  return new Set<Capability>(user.capabilities ?? ROLE_DEFAULT_CAPABILITIES[user.role]);
}

/** Does this user hold one capability? */
export function has(user: AuthUser | null | undefined, capability: Capability): boolean {
  return myCapabilities(user).has(capability);
}

/** Does this user hold EVERY listed capability? */
export function hasAll(
  user: AuthUser | null | undefined,
  ...capabilities: readonly Capability[]
): boolean {
  const mine = myCapabilities(user);
  return capabilities.every((c) => mine.has(c));
}

/**
 * A capability-gated link may only admit a user the API will actually serve.
 *
 * TWO CLASSES OF ROUTE, and the difference is whose data the screen shows:
 *  - TENANT-ARTEFACT routes (quotes, private courses) read rows that BELONG to a
 *    tenant, so they demand one and answer a super_admin 400 TENANT_REQUIRED /
 *    403 — verified by execution, Brief 8b build step 0. Their predicates below
 *    require `tenantId` on top of the capabilities.
 *  - CONFIG routes (the pricing + commission consoles) resolve a super_admin to
 *    the primary tenant (resolveEffectiveTenant, pricing.controller.ts), so they
 *    genuinely serve them. No tenant condition — adding one would delete a
 *    working link.
 *
 * super_admin holds all nine capabilities, which is exactly why the tenant
 * condition can't be skipped: capabilities alone would show them two links that
 * can only fail.
 */
const hasTenant = (user: AuthUser | null | undefined): boolean => !!user?.tenantId;

/**
 * The quote builder. Needs create_quotes AND view_pricing: the course picker
 * reads the pricing endpoint, so a user without view_pricing reaches a builder
 * with nothing to pick — the "view without edit must never surface a broken
 * screen" rule, in its other direction.
 *
 * This is what a granted offshore user gains: their preset lacks view_pricing,
 * so they're out by default — but the exclusion now tracks the CAPABILITY, not
 * the role. Grant them view_pricing and the link appears, which is the whole
 * point of the brick.
 *
 * Tenant-artefact ⇒ needs a tenant (super_admin: 400 TENANT_REQUIRED).
 */
export function canSeeQuotes(user: AuthUser | null | undefined): boolean {
  return hasAll(user, 'create_quotes', 'view_pricing') && hasTenant(user);
}

/**
 * The agency-private catalogue. Tenant-artefact ⇒ needs a tenant (super_admin:
 * 403 FORBIDDEN — private-courses.controller.ts rejects a null tenantId; the
 * master catalogue is their equivalent screen).
 */
export function canSeePrivateCourses(user: AuthUser | null | undefined): boolean {
  return has(user, 'manage_catalogue') && hasTenant(user);
}

/**
 * The course-pricing console — an unconditional EDITOR (its rows are inputs and
 * its Save calls an edit_pricing endpoint), so it gates on edit_pricing, not
 * view_pricing. Admitting a view-only holder would surface a screen whose every
 * Save 403s: the exact failure the brief forbids.
 *
 * Config route ⇒ no tenant condition; super_admin resolves to the primary tenant
 * and is served.
 */
export function canSeePricingConsole(user: AuthUser | null | undefined): boolean {
  return has(user, 'edit_pricing');
}

/**
 * The commission console. edit_commission for the canSeePricingConsole reason:
 * it is an editor, and `view_commission` alone would render a form that cannot
 * save. `owner` and `accounts` hold view_commission and the API's GET guards
 * accept them — a READ-ONLY commission view is a real product idea, but it needs
 * a read-only mode built first, so it stays out of scope here rather than
 * shipping as a broken editor.
 *
 * Config route ⇒ no tenant condition.
 */
export function canSeeCommissionConsole(user: AuthUser | null | undefined): boolean {
  return has(user, 'edit_commission');
}

/**
 * The read-only commission-earnings screen (/app/commission/earnings, Brief
 * BUILD-COMMISSION-VIEW). Distinct from canSeeCommissionConsole: that gates the
 * EDITOR on edit_commission, this gates the read-only VIEW on view_commission —
 * so owner/accounts (who hold view_commission but not edit_commission) finally
 * get a commission surface, while consultant/offshore (who hold neither) stay
 * walled. super_admin/admin hold both and see the editor AND this report.
 *
 * The server (requireCapability('view_commission')) and the DB commission-view
 * wall (0024) are the real gates; this only decides what is worth showing.
 */
export function canSeeCommissionEarnings(user: AuthUser | null | undefined): boolean {
  return has(user, 'view_commission');
}

/**
 * The consultant's read-only pricing link (/app/pricing, Brief C1).
 *
 * ROLE **AND** CAPABILITY, deliberately — the two say different things and both
 * are needed:
 *  - the role picks the VARIANT: /app/pricing is the consultant's read-only
 *    view; admins have the console at /app/admin/pricing and must not get a
 *    second, lesser link to the same data.
 *  - the capability is the PERMISSION: revoke view_pricing and the link goes.
 *
 * Which is why its ROUTE GUARD stays role-gated while this link does not. That
 * is not an inconsistency: the guard answers "is this your variant?", this
 * answers "may you see pricing at all?". Both are true at once.
 */
export function canSeeConsultantPricingLink(user: AuthUser | null | undefined): boolean {
  return user?.role === 'consultant' && has(user, 'view_pricing');
}

/**
 * The agency Team screen (/app/admin/team, Brief T1). ROLE **AND** CAPABILITY,
 * like canSeeConsultantPricingLink — the two conditions answer different
 * questions and both must hold:
 *  - the ROLE is positioning: Team is the agency-admin's screen. super_admin is
 *    excluded on purpose — the team API answers them 400 TENANT_REQUIRED (proven
 *    by execution, Brief 8b), so a link could only ever fail.
 *  - manage_users is the PERMISSION: every team endpoint is
 *    requireCapability('manage_users'), which the `admin` preset LACKS
 *    (super_admin only). A bare admin used to get a link that only ever showed
 *    red 403 banners; the link now tracks the switch. Grant the override (as
 *    Keval has) and Team returns.
 *
 * The role condition is kept deliberately even though manage_users alone would
 * pick out the same users today: the UI stays STRICTER than the server
 * (fail-closed), so a granted non-admin gains no new surface. The server's
 * requireCapability is still the only gate — this predicate only decides what is
 * worth SHOWING.
 */
export function canSeeTeam(user: AuthUser | null | undefined): boolean {
  return user?.role === 'admin' && has(user, 'manage_users');
}

/**
 * The owner/accounts cross-branch quotes overview (/app/oversight/quotes,
 * Brief OA). Gated ONLY on view_all_branches — its first real UI use. Held by
 * admin/owner/accounts/super_admin, NOT by consultant/offshore, which is exactly
 * the set that should read cross-branch. No tenant condition here: the SECTION
 * that carries this link renders only for non-admin/non-super_admin (the
 * oversight roles always have a tenant), so admin/super_admin never see it even
 * though they hold the capability — they read quotes through their own screens.
 */
export function canSeeOversightQuotes(user: AuthUser | null | undefined): boolean {
  return has(user, 'view_all_branches');
}

/**
 * The owner/accounts read-only catalogue/pricing overview
 * (/app/oversight/catalogue, Brief OA) — the SAME net-fee-only pricing screen the
 * consultant sees, behind a different gate. view_pricing is the permission;
 * `!canSeeConsultantPricingLink` avoids a DOUBLE pricing link for a consultant
 * (who already has /app/pricing). owner/accounts are not consultants, so they get
 * this one. Commission-free: it reads only the view_pricing endpoint.
 */
export function canSeeCatalogueOverview(user: AuthUser | null | undefined): boolean {
  return has(user, 'view_pricing') && !canSeeConsultantPricingLink(user);
}
